// Who can approve a service agreement quote's approval steps, and what to say about the rest.
// Mirrors the approve branch of service_agreement_quote_action: a manager or admin approves the
// override step (no band) with a reason, or a band step assigned to their profile or app role.

export type AgreementApprovalStep = {
  band_id: string | null;
  approver_profile_id: string | null;
  approver_role: string | null;
  status: string;
  band?: { approver_label: string | null; policy?: { name: string | null } | null } | null;
};

export type AgreementApprovalViewer = {
  profileId: string | null;
  roleCodes: readonly string[];
  isManager: boolean;
};

export const isOverrideApprovalStep = (step: AgreementApprovalStep) => step.band_id == null;

export function isAgreementApprovalStepForViewer(
  step: AgreementApprovalStep,
  viewer: AgreementApprovalViewer,
): boolean {
  if (!viewer.isManager || step.status !== 'pending') return false;
  if (isOverrideApprovalStep(step)) return true;
  if (step.approver_profile_id) return step.approver_profile_id === viewer.profileId;
  const role = step.approver_role?.toLowerCase();
  return !!role && viewer.roleCodes.some((code) => code.toLowerCase() === role);
}

export function agreementApprovalTask(
  steps: readonly AgreementApprovalStep[],
  viewer: AgreementApprovalViewer,
): { canApprove: boolean; needsReason: boolean } {
  const mine = steps.filter((step) => isAgreementApprovalStepForViewer(step, viewer));
  return { canApprove: mine.length > 0, needsReason: mine.length > 0 && mine.every(isOverrideApprovalStep) };
}

function roleLabel(step: AgreementApprovalStep): string {
  const label = step.band?.approver_label?.trim();
  if (label) return label;
  const role = (step.approver_role ?? '').replace(/_/g, ' ').trim();
  return role.charAt(0).toUpperCase() + role.slice(1);
}

/** One sentence on who a pending quote is waiting on, or null once nothing is pending. */
export function agreementApprovalWaitingOn(
  steps: readonly AgreementApprovalStep[],
  viewer: AgreementApprovalViewer,
  profileNames: Readonly<Record<string, string>> = {},
): string | null {
  const pending = steps.filter((step) => step.status === 'pending');
  if (!pending.length) return null;
  if (pending.some(isOverrideApprovalStep)) {
    return 'No approval policy covers this quote, so any manager can approve it with a reason.';
  }
  if (pending.some((step) => !step.approver_profile_id && !step.approver_role)) {
    return 'An approval step has no approver. Cancel the quote, fix the policy in Quote Approvals, then quote it again.';
  }
  const approvers = Array.from(
    new Set(
      pending.map((step) => {
        if (isAgreementApprovalStepForViewer(step, viewer)) return 'you';
        if (step.approver_profile_id) {
          return profileNames[step.approver_profile_id] || step.band?.approver_label?.trim() || 'the assigned approver';
        }
        return `anyone with the ${roleLabel(step)} role`;
      }),
    ),
  );
  const policies = Array.from(
    new Set(pending.map((step) => step.band?.policy?.name?.trim()).filter((name): name is string => !!name)),
  );
  return `Waiting on ${approvers.join(' and ')}${policies.length ? ` (${policies.join(', ')})` : ''}.`;
}
